import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from '@plone/volto/helpers';

const slides = [
  {
    title: 'Polyglot Monster Machine',
    subtitle: 'Building regulatory compliance infrastructure on Nick/Volto',
    body: 'Mack Palomäki — LJUSA TIDER AB\nBucharest, March 2026',
    style: 'title',
  },
  {
    title: 'This is not a CMS project',
    body: `I am building a post-market surveillance platform for products and services imported into the EU.

Plone, Volto, and Nick are building blocks — not the product.

The product is a system where importers, manufacturers, EU authorized representatives, and market surveillance authorities manage ongoing compliance obligations with full regulatory traceability across all EU official languages.`,
  },
  {
    title: 'Why language is THE foundation',
    body: `A global platform is impossible without precise, validated language because:

• You cannot comply with regulations you cannot express precisely in the local language
• You cannot negotiate B2B relationships across borders without shared terminology
• You cannot audit what you cannot describe unambiguously
• You cannot govern data across jurisdictions without legally precise language in each

A mistranslated safety warning kills someone.
A mistranslated contract term costs millions.
A mistranslated regulatory submission gets rejected by the notified body.`,
  },
  {
    title: 'The problem nobody is solving well',
    body: `EU regulations require safety-critical information in the official language(s) of each member state where a product is placed on the market.

For an importer placing products across the EU, that means up to 23 languages, each with legal liability.

The market is fragmented:
• Translation agencies — no regulatory awareness
• Regulatory consultants — no translation infrastructure

LJUSA TIDER builds both in one system.`,
  },
  {
    title: 'What Polyglot has already proven',
    body: `• 71 languages, ~330,000 translations across 4 platforms
• ISO 14971 risk classification (S1–S4) for 4,881 messages
• ISO 704 concept definitions for every source string
• IATE + Microsoft terminology cross-referencing
• AI review findings with interactive disposition workflow
• Full audit trail on every translation change
• CAPA tracking (corrective/preventive actions)
• Automated .po export matching upstream repo format

These patterns — audit trails, risk classification, quality gates, CAPA — are the same patterns needed for compliance document management.`,
  },
  {
    title: 'The tech stack',
    body: `Backend:  Nick (TypeScript, plone.restapi-compatible)
Frontend: Volto with volto-polyglot addon
Database: PostgreSQL — single source of truth
Hosting:  Hetzner (EU sovereign, GDPR)
Git:      Forgejo (self-hosted, EU sovereign)

Everything read-only and live from the database.
No static exports. No stale dashboards.
Every page reflects current state.`,
    style: 'mono',
  },
  {
    title: 'Geopolitically agnostic',
    body: `The platform is designed so that:

• Personal data stays in jurisdiction (GDPR, PIPL, LGPD)
• Distributed PostgreSQL — federated, not centralized
• No dependency on US cloud providers for production
• EU-sovereign hosting and git infrastructure
• "Need to know" embedded in architecture

This is not anti-anyone. It is pro-compliance.
Every jurisdiction has sovereignty requirements.
The architecture respects all of them.`,
  },
  {
    title: 'AI as a colleague, not a shortcut',
    body: `278 working sessions with Claude — not prompts, sessions.

The AI is a full participant in:
• Code generation (backend endpoints, React components, SQL)
• Translation quality review (6,088 findings across 32 languages)
• ISO 704 concept definitions for 1,431 messages
• Documentation (800+ documents in the knowledge base)

But every change goes through the same compliance gates:
audit trails, change control, human review.

AI generates → System validates → Human approves → AI executes`,
  },
  {
    title: 'Live demo',
    subtitle: 'dev.ljusa-tider.se',
    body: `Translation Browser — concept-first, 71 languages per message
Review Queue — interactive QC findings with disposition
Glossary — IATE + Microsoft terminology search
Knowledge Base — 800+ searchable documents
QMS — ISO 13485 document control with external standards

Everything you see is live from PostgreSQL.`,
    style: 'title',
  },
];

const Presentation = () => {
  const [current, setCurrent] = useState(0);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
      e.preventDefault();
      setCurrent((c) => Math.min(c + 1, slides.length - 1));
    } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
      e.preventDefault();
      setCurrent((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Home') {
      setCurrent(0);
    } else if (e.key === 'End') {
      setCurrent(slides.length - 1);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const slide = slides[current];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: '#fff',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '4em 8em',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        cursor: 'default',
        userSelect: 'none',
      }}
      onClick={(e) => {
        if (e.clientX > window.innerWidth / 2) {
          setCurrent((c) => Math.min(c + 1, slides.length - 1));
        } else {
          setCurrent((c) => Math.max(c - 1, 0));
        }
      }}
    >
      <Helmet title={`${slide.title} — Polyglot Presentation`} />

      {slide.style === 'title' ? (
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '3.5em', fontWeight: 700, marginBottom: '0.4em', color: '#1a1a1a' }}>
            {slide.title}
          </h1>
          {slide.subtitle && (
            <div style={{ fontSize: '1.6em', color: '#2185d0', marginBottom: '1.5em' }}>
              {slide.subtitle}
            </div>
          )}
          {slide.body && (
            <div style={{ fontSize: '1.3em', color: '#555', whiteSpace: 'pre-line', lineHeight: 1.8 }}>
              {slide.body}
            </div>
          )}
        </div>
      ) : (
        <div>
          <h1 style={{ fontSize: '2.8em', fontWeight: 700, marginBottom: '0.6em', color: '#1a1a1a' }}>
            {slide.title}
          </h1>
          <div style={{
            fontSize: '1.35em',
            color: '#333',
            whiteSpace: 'pre-line',
            lineHeight: 1.7,
            ...(slide.style === 'mono' ? { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: '1.15em' } : {}),
          }}>
            {slide.body}
          </div>
        </div>
      )}

      {/* Slide counter */}
      <div style={{
        position: 'fixed',
        bottom: '1.5em',
        right: '2em',
        fontSize: '0.9em',
        color: '#ccc',
      }}>
        {current + 1} / {slides.length}
      </div>

      {/* Progress bar */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        height: '3px',
        background: '#2185d0',
        width: `${((current + 1) / slides.length) * 100}%`,
        transition: 'width 0.3s ease',
      }} />
    </div>
  );
};

export default Presentation;
